import useTranslation from "next-translate/useTranslation"
import Link from "next/link"

import Icon, { IconName } from "./Icon"

export interface IPageNavigationProps {
  className?: string
  classNameForATag?: string
  leftNavigation?: Navigation
  centerNavigation?: Navigation
  rightNavigation?: Navigation
}

export interface Navigation {
  href: string | { pathname: string; query: any }
  title: string
  icon?: IconName
}

enum Position {
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
}
/**
 * This component shows a common page navigation, consisting of up to three elements:
 * left, center, right.
 * All elements have a default but changeable Icon.
 *
 * Style-Guide: @see https://lindakhne358077.invisionapp.com/console/share/TFV3D4K2MZ7
 */
const PageNavigation = (props: IPageNavigationProps): JSX.Element => {
  const {
    className,
    classNameForATag,
    leftNavigation,
    rightNavigation,
    centerNavigation } = props
  const { t } = useTranslation("common")

  /**
   * In dependence of the position of the link, where will be an icon or no icon or the icon will be
   * placed before or afterwards the text(title of link)
   *
   * @param navigation contains link data (title, href)
   * @param position location of the link
   */
  const placeNavigationTitleAndIcon = (navigation: Navigation, position: Position): JSX.Element => {
    switch (position) {
      case Position.LEFT:
        return <><Icon name={navigation.icon || "arrow-left"} size={20} />
          <span className="page-navigation-btn__text"> {t(navigation.title)}</span></>
      case Position.CENTER:
        return <><span className="page-navigation-btn__text"> {t(navigation.title)}</span><Icon name={navigation.icon || "arrow-upward"} size={20} /></>
      case Position.RIGHT:
        return <><span className="page-navigation-btn__text"> {t(navigation.title)}</span>
          <Icon name={navigation.icon || "arrow-right"} size={20} /></>
    }
  }

  /*
  * Builds the navigationcomponent, which could be an empty link, if no href is provided
  */
  const buildNavigationComponent = (navigation: Navigation, position: Position): JSX.Element => {
    return navigation && navigation.title && navigation.href
      ? <Link href={navigation.href}>
        <a className={classNameForATag ? classNameForATag : "btn page-navigation-btn"}>
          {placeNavigationTitleAndIcon(navigation, position)}
        </a>
      </Link>
      // @todo: potential BUG: aria-hidden="true" hides content for readers!
      : <a aria-hidden="true" className={"disabled " + (classNameForATag ? classNameForATag : "btn page-navigation-btn")}>
        {placeNavigationTitleAndIcon(navigation, position)}
      </a>
  }

  return <div className={className ? className : "page-navigation-area"}>
    {leftNavigation && <div id="leftNavigation" className="page-navigation-btn--left">
      {buildNavigationComponent(leftNavigation, Position.LEFT)}
    </div>}
    {centerNavigation && <div id="centerNavigation" className="page-navigation-btn--center">
      {buildNavigationComponent(centerNavigation, Position.CENTER)}
    </div>}
    {rightNavigation && <div id="rightNavigation" className="page-navigation-btn--right">
      {buildNavigationComponent(rightNavigation, Position.RIGHT)}
    </div>}
  </div>
}

export default PageNavigation